/**
 * Pricing Utilities - Staffelpreis-Berechnung
 * Zentrale Hilfsfunktionen für die Berechnung von gestaffelten Preisen
 */

import { TourTypePriceTier } from '../types/tourType.types';

/**
 * Tour Type Code Mappings
 * Maps frontend codes (English) to database codes (German) and vice versa
 * This handles the case where frontend uses 'WEDDING' but database might use 'HOCHZEIT'
 */
const TOUR_TYPE_ALIASES: { [key: string]: string[] } = {
  'WEDDING': ['HOCHZEIT', 'WEDDING', 'HOCHZEITSFAHRT'],
  'HOCHZEIT': ['WEDDING', 'HOCHZEIT', 'HOCHZEITSFAHRT'],
  'STANDARD': ['STANDARD', 'BASIC', 'BASISFAHRT'],
  'ALL_IN': ['ALL_IN', 'ALLIN', 'ALL-IN'],
  'ALL_IN_PREMIUM': ['ALL_IN_PREMIUM', 'ALLIN_PREMIUM', 'ALL-IN-PREMIUM', 'PREMIUM_ALL_IN', 'PREMIUM'],
  'EXCLUSIVE': ['EXCLUSIVE', 'EXKLUSIV', 'EXCLUSIV'],
};

export interface TourTypeWithPriceTiers {
  id: number;
  code: string;
  name: string;
  basePricePerPerson: number;
  priceTiers?: TourTypePriceTier[];
  pricingMode?: 'PerPerson' | 'FixedPrice';
  fixedPrice?: number;
}

export interface PriceCalculationResult {
  pricePerPerson: number;
  tierDescription: string;
  tierMinParticipants?: number;
  tierMaxParticipants?: number | null;
  pricingMode: 'PerPerson' | 'FixedPrice';
  fixedPrice?: number;
}

/**
 * Berechnet den gestaffelten Preis pro Person basierend auf der Teilnehmerzahl
 *
 * @param tourTypeCode - Der Code des Tour-Typs (z.B. "STANDARD", "PREMIUM_ALL_IN_01")
 * @param participants - Anzahl der Teilnehmer
 * @param tourTypes - Liste aller Tour-Typen mit ihren Staffelpreisen
 * @param fallbackPrice - Fallback-Preis falls kein passender Tour-Typ gefunden wird
 * @returns Preis pro Person und Staffel-Beschreibung
 */
export function calculateTieredPrice(
  tourTypeCode: string,
  participants: number,
  tourTypes: TourTypeWithPriceTiers[],
  fallbackPrice: number = 0
): PriceCalculationResult {
  // Finde den passenden Tour-Typ mit flexibler Zuordnung (WEDDING <-> HOCHZEIT etc.)
  const tourType = tourTypes.find(tt => {
    // Check both code and name against the search code (with aliases)
    return tourTypeCodesMatch(tt.code, tourTypeCode) || tourTypeCodesMatch(tt.name, tourTypeCode);
  });

  // Debug logging for troubleshooting
  console.log(`[pricingUtils] Searching for tour type '${tourTypeCode}' in ${tourTypes.length} tour types`);
  console.log(`[pricingUtils] Available tour types:`, tourTypes.map(tt => `${tt.code}/${tt.name} (pricingMode=${tt.pricingMode}, fixedPrice=${tt.fixedPrice})`));

  if (!tourType) {
    console.log(`[pricingUtils] Tour type '${tourTypeCode}' not found, using fallback price: ${fallbackPrice}`);
    return {
      pricePerPerson: fallbackPrice,
      tierDescription: 'Standardpreis',
      pricingMode: 'PerPerson'
    };
  }

  // FESTPREIS-MODUS: Wenn FixedPrice, den Festpreis zurückgeben
  if (tourType.pricingMode === 'FixedPrice' && tourType.fixedPrice && tourType.fixedPrice > 0) {
    console.log(`[pricingUtils] Tour type '${tourTypeCode}' is FESTPREIS: ${tourType.fixedPrice}€ total`);
    return {
      pricePerPerson: tourType.fixedPrice, // Bei Festpreis ist das der Gesamtpreis
      tierDescription: 'Festpreis',
      pricingMode: 'FixedPrice',
      fixedPrice: tourType.fixedPrice
    };
  }

  // PRO-PERSON-MODUS: Staffelpreise oder Basispreis
  const priceTiers = tourType.priceTiers?.filter(t => t.isActive) || [];

  if (priceTiers.length === 0) {
    // Keine Staffelpreise vorhanden - Basispreis verwenden
    console.log(`[pricingUtils] No price tiers for '${tourTypeCode}', using base price: ${tourType.basePricePerPerson}`);
    return {
      pricePerPerson: tourType.basePricePerPerson,
      tierDescription: 'Basispreis',
      pricingMode: 'PerPerson'
    };
  }

  // Sortiere nach MinParticipants
  const sortedTiers = [...priceTiers].sort((a, b) => a.minParticipants - b.minParticipants);

  // Finde die passende Staffel
  const applicableTier = sortedTiers.find(tier =>
    participants >= tier.minParticipants &&
    (tier.maxParticipants === null || tier.maxParticipants === undefined || participants <= tier.maxParticipants)
  );

  if (applicableTier) {
    const tierDescription = applicableTier.maxParticipants
      ? `${applicableTier.minParticipants}-${applicableTier.maxParticipants} Personen`
      : `${applicableTier.minParticipants}+ Personen`;

    console.log(`[pricingUtils] Found tier for ${participants} participants: ${tierDescription} = ${applicableTier.pricePerPerson}€`);

    return {
      pricePerPerson: applicableTier.pricePerPerson,
      tierDescription,
      tierMinParticipants: applicableTier.minParticipants,
      tierMaxParticipants: applicableTier.maxParticipants,
      pricingMode: 'PerPerson'
    };
  }

  // Kein passender Tier gefunden - Basispreis verwenden
  console.log(`[pricingUtils] No applicable tier for ${participants} participants, using base price: ${tourType.basePricePerPerson}`);
  return {
    pricePerPerson: tourType.basePricePerPerson,
    tierDescription: 'Basispreis',
    pricingMode: 'PerPerson'
  };
}

/**
 * Normalisiert einen Tour-Type-Code für konsistenten Vergleich
 */
export function normalizeTourTypeCode(code: string): string {
  if (!code) return '';
  return code
    .toUpperCase()
    .replace(/[_\s-]+/g, '_')
    .replace(/[^A-Z0-9_]/g, '');
}

/**
 * Get all possible aliases for a tour type code
 * This allows matching 'WEDDING' with 'HOCHZEIT' in the database
 */
export function getTourTypeAliases(code: string): string[] {
  const normalized = normalizeTourTypeCode(code);
  const aliases = TOUR_TYPE_ALIASES[normalized];
  if (aliases) {
    return aliases.map(normalizeTourTypeCode);
  }
  return [normalized];
}

/**
 * Check if two tour type codes match (considering aliases)
 */
export function tourTypeCodesMatch(code1: string, code2: string): boolean {
  const normalized1 = normalizeTourTypeCode(code1);
  const normalized2 = normalizeTourTypeCode(code2);

  // Direct match
  if (normalized1 === normalized2) return true;

  // Check aliases for code1
  const aliases1 = getTourTypeAliases(code1);
  if (aliases1.includes(normalized2)) return true;

  // Check aliases for code2
  const aliases2 = getTourTypeAliases(code2);
  if (aliases2.includes(normalized1)) return true;

  return false;
}

/**
 * Formatiert einen Preis im deutschen Währungsformat
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
}

/**
 * Gibt den niedrigsten Staffelpreis für einen Tour-Typ zurück
 * Nützlich für "ab X €" Anzeigen
 */
export function getLowestTierPrice(
  tourTypeCode: string,
  tourTypes: TourTypeWithPriceTiers[],
  fallbackPrice: number = 0
): number {
  const normalizedCode = normalizeTourTypeCode(tourTypeCode);

  const tourType = tourTypes.find(tt => {
    const ttCodeNormalized = normalizeTourTypeCode(tt.code);
    const ttNameNormalized = normalizeTourTypeCode(tt.name);
    return ttCodeNormalized === normalizedCode || ttNameNormalized === normalizedCode;
  });

  if (!tourType) {
    return fallbackPrice;
  }

  const priceTiers = tourType.priceTiers?.filter(t => t.isActive) || [];

  if (priceTiers.length === 0) {
    return tourType.basePricePerPerson;
  }

  // Finde den niedrigsten Preis
  return Math.min(...priceTiers.map(t => t.pricePerPerson));
}
